import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bug, X, Send, Clock, ChevronDown, Camera, RotateCcw, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';
const COOLDOWN_MS = 10 * 60 * 1000;
const COOLDOWN_KEY = 'bug_report_last_submit';
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5 MB

const CATEGORIES = [
  { value: 'Bug', label: 'Bug / Broken Feature' },
  { value: 'Upload', label: 'Upload Issue' },
  { value: 'Page', label: 'Page Not Loading' },
  { value: 'Performance', label: 'Slow / Laggy' },
  { value: 'Other', label: 'Other' },
];

const BugReportWidget = ({ user }) => {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('Bug');
  const [message, setMessage] = useState('');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Screenshot state
  const [wantScreenshot, setWantScreenshot] = useState(false);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const modalRef = useRef(null);

  // Check if bug reports are enabled
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/public/settings`);
        if (!cancelled) setEnabled(data.bug_report_enabled === true);
      } catch { /* silent */ }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // Cooldown timer
  useEffect(() => {
    const tick = () => {
      const last = parseInt(localStorage.getItem(COOLDOWN_KEY) || '0', 10);
      const remaining = Math.max(0, COOLDOWN_MS - (Date.now() - last));
      setCooldownRemaining(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const isOnCooldown = cooldownRemaining > 0;
  const cooldownMinutes = Math.ceil(cooldownRemaining / 60000);

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [screenshotPreview]);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    try {
      // Dynamically import html2canvas (code-split)
      const html2canvas = (await import('html2canvas')).default;

      // Hide the modal so it doesn't appear in the screenshot
      const modalEl = modalRef.current;
      if (modalEl) modalEl.style.visibility = 'hidden';

      // Wait one frame for the browser to paint without the modal
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      const root = document.getElementById('root') || document.body;
      const canvas = await html2canvas(root, {
        useCORS: true,
        allowTaint: true,
        scale: Math.min(window.devicePixelRatio, 2),
        width: Math.min(root.scrollWidth, 1280),
        height: Math.min(root.scrollHeight, 3000),
        windowWidth: Math.min(window.innerWidth, 1280),
        logging: false,
        imageTimeout: 5000,
      });

      // Restore modal
      if (modalEl) modalEl.style.visibility = '';

      // Convert to JPEG blob (quality 0.75)
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.75));
      if (!blob) throw new Error('Canvas toBlob returned null');

      if (blob.size > MAX_SCREENSHOT_BYTES) {
        // Retry at lower quality
        const smallBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.5));
        if (smallBlob && smallBlob.size <= MAX_SCREENSHOT_BYTES) {
          setScreenshotBlob(smallBlob);
          if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
          setScreenshotPreview(URL.createObjectURL(smallBlob));
        } else {
          toast.error('Screenshot too large even after compression. Try on a smaller page.');
          return;
        }
      } else {
        setScreenshotBlob(blob);
        if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
        setScreenshotPreview(URL.createObjectURL(blob));
      }

      toast.success('Screenshot captured');
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      // Restore modal if it was hidden
      if (modalRef.current) modalRef.current.style.visibility = '';
      toast.error('Screenshot capture failed on this device — please upload manually.');
    } finally {
      setCapturing(false);
    }
  }, [screenshotPreview]);

  const removeScreenshot = useCallback(() => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotBlob(null);
    setScreenshotPreview(null);
  }, [screenshotPreview]);

  const handleSubmit = useCallback(async () => {
    if (submitting || isOnCooldown) return;
    if (message.trim().length < 20) {
      toast.error('Please describe the issue in at least 20 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      // Upload screenshot first if present
      let screenshot_url = null;
      if (screenshotBlob) {
        const formData = new FormData();
        formData.append('file', screenshotBlob, 'bug-report-screenshot.jpg');
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        screenshot_url = uploadRes.data?.url;
      }

      const body = {
        category,
        message: message.trim(),
        page_url: window.location.href,
        app_version: document.querySelector('meta[name="version"]')?.content || 'web',
        screenshot_url,
      };
      if (includeMetadata) {
        body.viewport = { width: window.innerWidth, height: window.innerHeight };
      }

      await axios.post(`${API}/bug-reports`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });

      localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
      setCooldownRemaining(COOLDOWN_MS);
      toast.success('Report sent to admin — thank you!');
      setMessage('');
      setCategory('Bug');
      removeScreenshot();
      setWantScreenshot(false);
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send report';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }, [category, message, includeMetadata, submitting, isOnCooldown, screenshotBlob, removeScreenshot]);

  // Don't render for admins or when disabled
  if (!enabled || !user) return null;
  if (user.role === 'admin' || user.is_admin) return null;

  return createPortal(
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 z-[9990] flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700 rounded-full px-4 py-2.5 shadow-lg transition-all duration-200 text-sm font-medium"
          data-testid="bug-report-trigger"
        >
          <Bug className="w-4 h-4" />
          Report Issue
        </button>
      )}

      {/* Modal */}
      {open && (
        <div ref={modalRef} className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" data-testid="bug-report-modal">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />

          {/* Panel */}
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-t-2xl sm:rounded-2xl shadow-2xl mx-auto sm:mx-4 max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-orange-400" />
                <h3 className="font-semibold text-white text-base">Report an Issue</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white p-1 rounded transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1.5">Category</label>
                <div className="relative">
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2.5 text-sm appearance-none cursor-pointer focus:border-orange-500 focus:outline-none"
                    data-testid="bug-report-category"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1.5">
                  What happened? <span className="text-zinc-600">(min 20 chars)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 2000))}
                  placeholder="Describe the issue you encountered..."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg px-3 py-2.5 text-sm resize-none focus:border-orange-500 focus:outline-none placeholder:text-zinc-600"
                  data-testid="bug-report-message"
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${message.length < 20 ? 'text-zinc-600' : 'text-zinc-500'}`}>
                    {message.length}/2000
                  </span>
                  {message.length > 0 && message.length < 20 && (
                    <span className="text-xs text-amber-500">{20 - message.length} more chars needed</span>
                  )}
                </div>
              </div>

              {/* Screenshot section */}
              <div className="space-y-2">
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={wantScreenshot}
                    onChange={(e) => {
                      setWantScreenshot(e.target.checked);
                      if (!e.target.checked) removeScreenshot();
                    }}
                    className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
                    data-testid="bug-report-screenshot-checkbox"
                  />
                  <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                    Include screenshot
                  </span>
                </label>

                {wantScreenshot && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 space-y-3">
                    {screenshotPreview ? (
                      <>
                        <img
                          src={screenshotPreview}
                          alt="Screenshot preview"
                          className="w-full rounded border border-zinc-700 max-h-32 object-contain bg-zinc-950"
                          data-testid="screenshot-preview"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={captureScreenshot}
                            disabled={capturing}
                            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded px-2.5 py-1.5 transition-colors"
                            data-testid="screenshot-retake-btn"
                          >
                            <RotateCcw className="w-3 h-3" /> Retake
                          </button>
                          <button
                            onClick={removeScreenshot}
                            className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 bg-zinc-700 hover:bg-zinc-600 rounded px-2.5 py-1.5 transition-colors"
                            data-testid="screenshot-remove-btn"
                          >
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </>
                    ) : (
                      <button
                        onClick={captureScreenshot}
                        disabled={capturing}
                        className="w-full flex items-center justify-center gap-2 text-sm text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 rounded-lg py-2.5 transition-colors disabled:opacity-50"
                        data-testid="screenshot-capture-btn"
                      >
                        {capturing ? (
                          <>
                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-400/30 border-t-zinc-400" />
                            Capturing...
                          </>
                        ) : (
                          <>
                            <Camera className="w-4 h-4" />
                            Capture Screenshot
                          </>
                        )}
                      </button>
                    )}
                    <p className="text-xs text-zinc-600 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                      Screenshot may include personal info visible on screen.
                    </p>
                  </div>
                )}
              </div>

              {/* Metadata checkbox */}
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
                  data-testid="bug-report-metadata-checkbox"
                />
                <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
                  Include device &amp; page info to help debug
                </span>
              </label>

              {/* Safety disclaimer */}
              <p className="text-xs text-zinc-600 leading-relaxed">
                Avoid including passwords or private info in your message or screenshot.
              </p>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-zinc-800">
              {isOnCooldown ? (
                <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm py-2">
                  <Clock className="w-4 h-4" />
                  <span>You can submit again in ~{cooldownMinutes} min</span>
                </div>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || message.trim().length < 20}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-40"
                  data-testid="bug-report-submit"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                      {screenshotBlob ? 'Uploading screenshot...' : 'Sending...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Send Report{screenshotBlob ? ' with Screenshot' : ''}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default BugReportWidget;
